<template>
  <div class="not-found">
    <h1>{{ $t("Subscribe") }}</h1>
    <p v-if="!failed">
      <i class="fas fa-spinner fa-spin me-2"></i>
      {{
        $t(
          "Please wait, we are performing some required actions to let you in...",
        )
      }}
    </p>
    <p v-else>
      {{ $t("Subscription verification failed. Please try again.") }}
    </p>

    <router-link to="/" class="cmn--btn">
      <span>{{ $t("Go Back Home") }}</span>
    </router-link>
  </div>
</template>

<script>
import { mapActions } from "pinia";
import { useAuthStore } from "@/store/auth";

const SUBSCRIBE_START_KEY = "subscribe_start_time";
const SUBSCRIBE_MSISDN_KEY = "subscribe_msisdn";
const MAX_RETRY_MS = 20000; // 20 seconds
const RETRY_INTERVAL_MS = 3000; // 3 seconds

export default {
  name: "SubscribeView",
  data() {
    return {
      failed: false,
      retryTimer: null,
    };
  },
  methods: {
    ...mapActions(useAuthStore, ["subscribe"]),

    cleanup() {
      localStorage.removeItem(SUBSCRIBE_START_KEY);
      localStorage.removeItem(SUBSCRIBE_MSISDN_KEY);
      if (this.retryTimer) clearTimeout(this.retryTimer);
    },

    redirectOnSuccess() {
      const redirectUrl = localStorage.getItem("redirectUrl");
      localStorage.removeItem("redirectUrl");
      this.$router.push(redirectUrl || "/");
    },

    async attemptSubscribe(phone, startTime) {
      const elapsed = Date.now() - startTime;
      if (elapsed >= MAX_RETRY_MS) {
        console.warn("[Subscribe] Retry window expired after 1 minute");
        this.cleanup();
        this.failed = true;
        return;
      }

      try {
        const response = await this.subscribe(phone);
        console.log("[Subscribe] Success:", response?.data);
        this.cleanup();
        this.redirectOnSuccess();
      } catch (error) {
        console.warn(
          "[Subscribe] Attempt failed:",
          error?.response?.status,
          error?.response?.data?.message || error?.message,
          "— retrying in " + RETRY_INTERVAL_MS / 1000 + "s",
        );
        this.retryTimer = setTimeout(() => {
          this.attemptSubscribe(phone, startTime);
        }, RETRY_INTERVAL_MS);
      }
    },
  },

  async created() {
    const urlParams = new URLSearchParams(window.location.search);
    let phone = urlParams.get("msisdn");
    let startTime;

    if (phone) {
      // Fresh redirect from external auth service
      startTime = Date.now();
      localStorage.setItem(SUBSCRIBE_START_KEY, startTime.toString());
      localStorage.setItem(SUBSCRIBE_MSISDN_KEY, phone);
      console.log("[Subscribe] Fresh redirect, msisdn:", phone);
    } else {
      // Possibly a refresh — restore from localStorage
      phone = localStorage.getItem(SUBSCRIBE_MSISDN_KEY);
      const stored = localStorage.getItem(SUBSCRIBE_START_KEY);
      startTime = stored ? parseInt(stored, 10) : null;
      console.log(
        "[Subscribe] Restore from localStorage, msisdn:",
        phone,
        "startTime:",
        startTime,
      );
    }

    if (!phone || !startTime) {
      console.warn("[Subscribe] No msisdn available, redirecting to home");
      this.cleanup();
      this.$router.push("/");
      return;
    }

    await this.attemptSubscribe(phone, startTime);
  },

  beforeUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  },
};
</script>

<style scoped>
.not-found {
  text-align: center;
  margin-top: 10vh;
  height: 50vh;
}
.not-found h1 {
  font-size: 6rem;
  color: #ff4040;
}
.not-found p {
  font-size: 1.5rem;
  margin: 1rem 0;
}
</style>
